import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import '../../assets/css/partner/partner.css';
import { CustomColumnDef, FilteringList } from '../../components/filteringList/FilteringList';
import { Form } from 'react-bootstrap';
import CompanyInfoCard, { CompanyInfo } from '../../components/companyInfo/CompanyInfoCard';
import OutputDatePicker from '../../components/outputDatePicker/OutputDatePicker';
import { Client } from '../../model/Client';
import { useNavigate, useParams, useLocation  } from 'react-router-dom';
import { buildSelectedClientQuery } from '../../utils/url';
import AlertDialog from '../../components/alertDialog';
import { roles } from '../../utils/define';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const initialMyCompany:CompanyInfo = {
  infoKind: "",
  companyName: "",
  organization: "",
  address: '',
  phone: '',
  agreement: {
    startDate: '',
    endDate: ''
  },
  staffName: '',
  staffNameKana: '',
  staffPhone: '',
  staffMail: '',
  sysStatus: '',
}

const ClientsPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();

  // 認証情報がない場合はログインページへリダイレクト
  useEffect(() => {
    if (!authData) {
      navigate('/login');
    }
  }, [authData]);

  const [selectedRows, setSelectedRows] = React.useState({});
  const { partner_id } = useParams<{ partner_id: string }>();
  const location = useLocation();
  const [adminId, setAdminId] = React.useState(
    location.state?.adminId || authData.admin_id
  );
  const [sysStatus, setSysStatus] = React.useState(
    location.state?.sysStatus || authData.sys_status
  );
  
  const [clientListTitle, setClientListTitle] = React.useState(() => {
    if(sysStatus === 0) {
      return "お客様一覧";
    } else {
      return "店舗一覧";
    }
  });
  const [isSysAdmin, setIsSysAdmin] = React.useState(authData.roles.includes(roles.sys_admin));
  
  interface client {
    id: string;
    partnerId: string;
    clientId: string;
    name: string;
    number: string;
    vehicleType: string;
  }

  const [vehicleCountListTitle, setVehicleCountListTitle] = React.useState(() => {
    if(sysStatus === 0) {
      return "車両数";
    } else {
      return "在車台数";
    }
  });
    
  const clientListColumns:CustomColumnDef<Client>[] = React.useMemo<CustomColumnDef<Client>[]>(
      () => {        
        if(sysStatus === 0 || sysStatus === 1) {
          // テレマ
          return [
            {
              id: 'select',
              hasCheckbox: true,
              header: ({ table }) => (
                <Form.Check
                  //現在のページの全ての行が選択されているかどうか
                  checked={table.getIsAllRowsSelected()}
                  //全ての行のチェックボックスを切り替えるために使用するハンドラーを返す
                  onChange={table.getToggleAllRowsSelectedHandler()}
                />
              ),
              cell: ({ row }) => (
                <Form.Check
                  //行が選択されているかどうか
                  checked={row.getIsSelected()}
                  //チェックボックスを切り替えるために使用するハンドラーを返す
                  onChange={row.getToggleSelectedHandler()}
                />
              )
            },
            {
              accessorFn: row => row.company,
              id: 'company',
              cell: info => info.getValue(),
              header: () => <span>会社名</span>,
            },
            {
              accessorFn: row => row.organization,
              id: 'organization',
              cell: info => info.getValue(),
              header: () => <span>テナントID</span>,
            },
            {
              accessorFn: row => row.staffEmail,
              id: 'staffEmail',
              cell: info => info.getValue(),
              header: () => <span>担当者メールアドレス</span>,
            },
            {
              accessorFn: row => row.staffName,
              id: 'staffName',
              cell: info => info.getValue(),
              header: () => <span>担当者指名</span>,
            },
            {
              accessorKey: 'vehicleCount',
              header: () => <span>{vehicleCountListTitle}</span>,
              meta: {
                filterVariant: 'range',
              },
            },
          ]
        } else {
          return [
            {
              accessorFn: row => row.company,
              id: 'company',
              cell: info => info.getValue(),
              header: () => <span>会社名</span>,
            },
            {
              accessorFn: row => row.organization,
              id: 'organization',
              cell: info => info.getValue(),
              header: () => <span>テナントID</span>,
            },
            {
              accessorFn: row => row.staffEmail,
              id: 'staffEmail',
              cell: info => info.getValue(),
              header: () => <span>担当者メールアドレス</span>,
            },
            {
              accessorFn: row => row.staffName,
              id: 'staffName',
              cell: info => info.getValue(),
              header: () => <span>担当者指名</span>,
            },
          ]
        }
      },
      [sysStatus]
    );

  const today = new Date()
  const yesterday = new Date(today.setDate(today.getDate() - 1))
  const [outputDate, setOutputDate] = React.useState(yesterday);
  const [myCompany, setMyCompany] = React.useState(initialMyCompany);
  const [clientList, setClientList] = React.useState([]);
  const [alertDialogShow, setAlertDialogShow] = React.useState(false);

  React.useEffect(() => {
    interface client {
      id: string;
      company: string;
      organization: string;
      sys_status: string;
      staff_email: string;
      staff_name: string;
      client_count: string;
      vehicle_count: string;
    }

    if(partner_id && adminId){
      axios.get(`${API_BASE_URL}/partners/${partner_id}/clients?admin_id=${adminId}`)
      .then(response => {
        if (response.status === 200) {
          console.log(`api call success endpoint: ${API_BASE_URL}/partners/${partner_id}/clients?admin_id=${adminId}`)
          let list =  response.data.clients.map((obj: client) => ({            
            isCheck: false,
            id: obj.id,
            company: obj.company,
            organization: obj.organization,
            staffEmail: obj.staff_email,
            staffName: obj.staff_name,
            vehicleCount: obj.vehicle_count,
          }));
          setClientList(list);

          setMyCompany({
            infoKind: "販売会社",
              companyName: response.data.partner.company,
              organization: response.data.partner.organization,
              address: response.data.partner.address,
              phone: response.data.partner.phone_number,
              agreement: {
                startDate: response.data.partner.agreement_start_date,
                endDate: response.data.partner.agreement_end_date
              },
              staffName: response.data.partner.staff_name,
              staffNameKana: response.data.partner.staff_name_kana,
              staffPhone: response.data.partner.staff_phone_number || response.data.partner.staff_phone_number !== '' ? response.data.partner.staff_phone_number: "設定なし",
              staffMail: response.data.partner.staff_email || response.data.partner.staff_email !== '' ? response.data.partner.staff_email : "設定なし",
              sysStatus: response.data.partner.sys_status,
          });
          setSysStatus(response.data.partner.sys_status);
          if(response.data.partner.sys_status === 2) {
            // 運転者通知の場合、全クライアントを対象とする
            setSelectedRows(list);
          }
        } else {
          console.error('get partners failed:', response.data.message || 'Unknown error');
        }
      })
      .catch(error => {
        console.error('Error during login API call:', error);
      });
    }    
  }, [adminId, partner_id])

  return (
    <div className="mx-auto my-2" style={{width: "99%"}}>
      {isSysAdmin && <button type="button" className="btn ps-0 pt-2 pb-2 mb-0 mr-auto fs-6 d-flex align-items-center" onClick={() => navigate(-1)}>
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" className="me-1 bi bi-chevron-left" viewBox="0 0 16 16">
          <path fill-rule="evenodd" d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"/>
        </svg>
        販売会社一覧
      </button>}    
      <CompanyInfoCard companyInfo={myCompany} />
      <div className="p-1">
        <h5 className='p-2 mb-0 mr-auto' style={{width: "fit-content"}}>{clientListTitle}</h5>
        <OutputDatePicker 
          selectedMonth={outputDate} 
          onChange={(selected) => {
            setOutputDate(selected === null ? outputDate : selected)
          }} 
          onClickDownload={(event) => {
            if(Object.keys(selectedRows).length !== 0) {
              const clientId = buildSelectedClientQuery(selectedRows as Client[]);
              const year = outputDate.getFullYear();
              const month = outputDate.getMonth() + 1;
              window.open(`/report?year=${year}&month=${month}&${clientId}`, '_blank');
            } else {
                setAlertDialogShow(true);
            }
          }}
          className="output-date-picker"
          buttonDisabled={clientList.length === 0}
        />
      </div>
      <div style={{display: "flex", justifyContent: "center"}}>
        <FilteringList 
            columns={clientListColumns} 
            datas={clientList}
            sysStatus={sysStatus}
            onCheck={(selectedRows:Client[])=>{setSelectedRows(selectedRows);console.log("selected: " + JSON.stringify(selectedRows));}}
            onClick={(clickedRow:Client) => {
              console.log(`sysStatus:${sysStatus}`, "clicked: " + JSON.stringify(clickedRow))
              if (sysStatus === 0 || sysStatus === 1) {
                navigate(`/partner/${partner_id}/client/${clickedRow.id}`,{
                  state:{
                    adminId: authData.admin_id,
                    sysStatus: sysStatus,
                  }
                });
              }
            }}
          />
      </div>
      <AlertDialog 
        show={alertDialogShow} 
        message=<p>レポートを出力する会社を選択してください。</p> 
        okClickFunc={()=>setAlertDialogShow(false)}  />
    </div>
  );
}

export default ClientsPage;